import React from 'react';
import { Button, TextSelect } from '../../components';
import { Request } from '../../utils';
import { SendCompensationDialog } from '../../components/compensation/SendCompensationDialog';
import { IconButton, Snackbar, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { getEditPlayerDataInfo, updatePlayerData } from '../../actions/player';

interface Props {
    readonly styled: StyledProps;
    readonly titleDataItems: Entities.TitleDataItem[];
    readonly liveEvents: Entities.LiveEvent[];
    readonly playerData: Entities.PlayerData[];
    readonly playerId: string;
}

interface State {
    readonly eventId: string;
    readonly openDialog: DialogType | null;
    readonly selectedReward: Reward | null;
    readonly info: string,
}

interface Reward {
    _gachaKeyRewardId: string;
    _milestoneId: string;
    _rewardId: string;
}

type DialogType = 'SendCompensation';

export class Milestone extends React.Component<Props, State> {
    eventIds = this.props.liveEvents?.map(liveEvent => {
        try {
            console.log(liveEvent.gameData);
            const decodedData = JSON.parse(liveEvent.gameData);
            return decodedData?.eventId;
        } catch (err) {
            console.error('Failed to decode game data', err);
            return undefined;
        }
    })?.filter(Boolean);

    state: Readonly<State> = {
        eventId: this.eventIds[0] ?? '',
        openDialog: null,
        selectedReward: null,
        info: '',
    };

    private readonly closeDialog = () => {
        this.setState({openDialog: null});
    }

    private readonly openSendCompensationDialog = (selectedReward: Reward) => this.setState({ selectedReward, openDialog: 'SendCompensation' });
    private readonly renderSendCompensationDialog = () => {
        const { openDialog, selectedReward } = this.state;
        if (!selectedReward) {
            return;
        }
        return (
            <SendCompensationDialog
                open={openDialog === 'SendCompensation'}
                styled={this.props.styled}
                title="Send Compensation"
                items={[
                    selectedReward._gachaKeyRewardId,
                    selectedReward._rewardId,
                ].filter(Boolean).map((key: string) => ({
                        Key: key,
                        Amount: 1,
                }))}
                user={this.props.playerId}
                onClose={this.closeDialog.bind(this)}
                onSend={this.sendReward.bind(this)}
                maxWidth={'xs'}
            />
        );
    };
    private readonly titleDataItems = this.props.titleDataItems;

    private readonly changeEventId = (eventId: string) => {
        this.setState({eventId});
    };
    private readonly setInfo = (info: string) => this.setState({ info });

    render() {
        const { info } = this.state;

        return <div style={{padding: 12, boxShadow: 'rgba(0,0,0,0.5) 6px 12px 10px 0px'}}>
            <h2>Milestones</h2>
            { this.renderEventSelector() }
            { this.renderMilestones() }
            {this.renderSendCompensationDialog()}

            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                open={Boolean(info)}
                autoHideDuration={600000}
                onClose={() => this.setInfo('')}
                message={
                    <Typography color="info" id="message-id">
                        {info}
                    </Typography>
                }
                action={[
                    <IconButton key="close" aria-label="Close" color="inherit" onClick={() => this.setInfo('')}>
                        <Close />
                    </IconButton>,
                ]}
            />
        </div>
    }

    private readonly bound = (min: number, max: number, input: number) : number =>
    {
        return Math.min(max, Math.max(min, input));
    }

    private readonly renderEventSelector = () => {
        return <TextSelect
            label='Event ID'
            value={this.state.eventId}
            onChange={this.changeEventId}
        >
            {
                this.eventIds.map(
                    (setting: string) => <option key={setting}>{setting}</option>
                )
            }
        </TextSelect>
    }

    private readonly renderMilestones = () => {
        const eventSettingsItem = this.titleDataItems.find(item => item.key === 'global_event_settings');
        const eventSettings: {_id: string, _eventContentId: string, _milestoneRewards: {_gachaKeyRewardId: string, _milestoneId: string, _rewardId: string}[]}[]|undefined = eventSettingsItem && JSON.parse(eventSettingsItem.value);
        const selectedEventSettings = eventSettings?.find((event: {_id: string}) => event._id === this.state.eventId);
        if (!selectedEventSettings) {
            return (
                <>Could not find Event Settings content</>
            );
        }
        const eventId = this.props.liveEvents.find(liveEvent => JSON.parse(liveEvent.gameData).eventId === this.state.eventId)?.eventId;
        if (!eventId) {
            return <></>;
        }
        const selectedEventMilestoneRewards = selectedEventSettings._milestoneRewards;
        const selectedMilestones = this.titleDataItems.find((item) => item.key === selectedEventSettings._eventContentId + '_milestone');
        const milestoneData = selectedMilestones && JSON.parse(selectedMilestones.value);
        const currencies = new Map<string,number>;
        milestoneData.forEach((milestone: {_currencyId: string}) => {
            if (currencies.has(milestone._currencyId)) {
                return;
            }
            const playerInventory = this.props.playerData.find((data) => {
                return data.key === eventId + '_' + milestone._currencyId;
            });
            const inventoryAmount = (playerInventory && JSON.parse(playerInventory.value)._amount) ?? 0;
            currencies.set(milestone._currencyId, inventoryAmount);
        });

        const milestones = [];
        let isMilestoneInProgress = true;
        for (let i = 0; i < milestoneData.length; i++) {
            const milestone = milestoneData[i];
            const milestoneProgress = this.bound(0, isMilestoneInProgress ? 1 : 0, (currencies.get(milestone._currencyId) ?? 0) / milestone._targetAmount);
            if (milestoneProgress < 1) {
                isMilestoneInProgress = false;
            }
            const nextMilestone = milestoneData[i + 1];
            const nextMilestoneProgress = this.bound(0, isMilestoneInProgress ? 1 : 0, nextMilestone && (currencies.get(nextMilestone._currencyId) ?? 0) / nextMilestone._targetAmount);
            const reward = selectedEventMilestoneRewards.find(reward => reward._milestoneId === milestone._id);
            milestones.push(
                <>
                    <div key={`info-milestone-${milestone._id}`} style={{
                        borderRadius: 3,
                        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                        borderColor: "rgba(209, 213, 219, 1)",
                        padding: 12,
                    }}>
                        Milestone {i + 1}<br />
                        {reward && 
                            <Button
                                style={{marginTop: 14}}
                                variant="text"
                                color="secondary"
                                text='Send Reward as Compensation'
                                onClick={() => this.openSendCompensationDialog(reward)}
                            />
                        }
                        <br />
                        <Button
                            style={{marginTop: 2}}
                            variant="text"
                            color="secondary"
                            text='Set Currency to Milestone Target'
                            onClick={() => this.updateCurrency(milestone, eventId)}
                        />
                    </div>
                    <div key={`chart-milestone-${milestone._id}`} style={{textAlign: 'center', height: '100%' }}>
                        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
                            <svg width="20" height="100%" xmlns="http://www.w3.org/2000/svg">

                                {/** Top half of the background bar */}
                                <rect x="0" y="0" width="20" height="50%" fill="lightgray" />

                                {/** Bottom half of the background bar */}
                                { i < milestoneData.length - 1 && <rect x="0" y="50%" width="20" height="50%" fill="lightgray" /> }

                                {/** Top half of the progress bar */}
                                { i === 0
                                    ? <rect x="0" y="0" width="20" fill="green" height={`${Math.floor(milestoneProgress * 50)}%`} />
                                    : <rect x="0" y="0" width="20" fill="green" height={`${Math.max(0, Math.floor((milestoneProgress - 0.5) * 100))}%`} />
                                }

                                {/** Bottom half of the progress bar */}
                                { i < milestoneData.length - 1 && <rect x="0" y="50%" width="20" height={`${Math.min(50, Math.floor(nextMilestoneProgress * 100))}%`} fill="green" /> }

                                {/** Milestone completion circle */}
                                {
                                    milestoneProgress >= 1
                                    ? <circle r="9" cx="10" cy="50%" fill="lightgreen" stroke="black" strokeWidth="1" />
                                    : <circle r="9" cx="10" cy="50%" fill="lightgray" stroke="black" strokeWidth="1" />
                                }
                            </svg>
                            {/** Progress text */}
                            <span style={{
                                writingMode: 'vertical-lr',
                                display: 'inline-block',
                                color: 'black',
                            }}>
                                { milestoneProgress > 0 && !isMilestoneInProgress
                                    && <>{this.formatLargeNumber(currencies.get(milestone._currencyId) ?? 0)} / {milestone._targetAmount.toExponential()}</>
                                    || <>&nbsp;</>
                                }
                            </span>
                        </div>
                    </div>
                </>
            );
        }

        return <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)'
        }}>{ milestones }</div>;
    }

    formatLargeNumber(input: number) : string {
        if (input < 1000) {
            return String(input);
        }
        return input.toExponential(2);
    }

    async sendReward(title: string, description: string, from: number, to: number): Promise<void> {
        const { selectedReward } = this.state;
        if (!selectedReward) {
            return;
        }
        await Request.post(`compensation/create`, {
            title,
            description,
            from: String(from),
            to: String(to),
            allUsers: false,
            userList: this.props.playerId,
            itemList: [
                selectedReward._rewardId,
                selectedReward._gachaKeyRewardId,
            ].filter(Boolean).map(reward => `${reward}:1`).join('|'),
        });
        this.setInfo('Compensation sent!');
    }

    async updateCurrency(milestone: { _currencyId: string; _targetAmount: number; _id: string; }, eventId: string): Promise<void> {
        const itemKey = eventId + '_' + milestone._currencyId;
        const playerInventory = this.props.playerData.find((data) => {
            return data.key === itemKey;
        });
        if (!playerInventory) {
            throw new Error('Failed to find inventory item: ' + eventId + '_' + milestone._currencyId);
        }
        const itemData = JSON.parse(playerInventory.value);
        itemData._amount = milestone._targetAmount;

        this.setInfo('Updated!');
        await updatePlayerData(this.props.playerId, itemKey, JSON.stringify(itemData));
        await getEditPlayerDataInfo(this.props.playerId);
    }
}
